<template>
  <div class="list-form-wrap">
    <div class="list-form" v-loading="isLoading">
      <div v-if="model.header.isVisible" class="list-form__header">
        <slot name="title" :title="model.header.title.value">
          <h1
            v-if="model.header.title.isVisible"
            class="list-form__header-title"
          >{{model.header.title.value}}</h1>
        </slot>
        <slot name="inner-tools"></slot>
        <div v-if="visibleHeaderControls && model.header.controls.isVisible" class="list-form__header-controls">
          <div v-if="isAddNew">
            <button type="btn" class="btn btn-size-s" @click="handlerCommandControls('addNew')">
              <i class="fas fa-plus"></i>
              {{model.header.controls.addNew.title}}
            </button>
          </div>
        </div>
      </div>
      <layout-content :isScroll="model.content.isScroll" :count="data.length">
        <list-form-item
          v-for="(item, i) in data"
          :model="model"
          :key="item[model.store.mainField]"
          :data="mapOrigin[item[model.store.mainField]]"
          :item="item"
          :isAutoSave="isAutoSave"
          :autoSaveUrl="model.autoSave.url"
          :formModel="formModel"
          :class="{'is-first-item': i === 0, 'is-last-item': data.length - 1 === i}"
          :isVisibleControls="isVisibleControls"
          :permissionControls="permissionControls"
          @commandControl="handlerCommandControls"
          @updateDataItem="handleUpdateDataItem"
          @onPatch="handlerPatch"
          @onAutoSaveSuccess="handleAutoSaveSuccess"
          @onAutoSaveError="handleAutoSaveError"
        >
          <template
            v-if="item._group && item._group.isFirstOfGroup"
            v-slot:first-of-group="{ item }"
          >
            <slot name="first-of-group" :item="item"></slot>
          </template>
          <template
            v-slot:default="{ data, autoSave, item, updateDataItem, updateModelItem, handleCommand, permissionControls }"
          >
            <slot
              :data="data"
              :item="item"
              :autoSave="autoSave"
              :updateDataItem="updateDataItem"
              :updateModelItem="updateModelItem"
              :handleCommand="handleCommand"
              :permissionControls="permissionControls"
            />
          </template>
          <template v-if="item._group && item._group.isLastOfGroup" v-slot:last-of-group="{ item }">
            <slot name="last-of-group" :item="item"></slot>
          </template>
        </list-form-item>
      </layout-content>
      <div class="list-form__footer" v-if="model.footer.isVisible">
        <pagination
          v-if="model.footer.pagination.isVisible"
          class="list-form__pagination"
          :config="model.footer.pagination"
          @on-pagination="handlerPagination"
        ></pagination>
      </div>
    </div>
    <dialog-delete
      ref="dialogDelete"
      :config="model.store.delete.modal"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    />
  </div>
</template>

<script>
import models from './models'
import ExcelExporter from '@/components/ExcelExporter'
import { createParams } from '@/utils/dynamic'
import { api } from '@/services/api'
import { keepToModel } from '@/utils/objects'

export default {
  name: 'ListForm',
  components: {
    'dialog-delete': () => import(/* webpackChunkName: 'Dialogs' */ '@/components/Dialogs/DialogConfirm'),
    'list-form-item': () => import(/* webpackChunkName: 'ListForm' */ './ListFormItem.vue'),
    'layout-content': () => import(/* webpackChunkName: 'ListForm' */ './LayoutContent.vue'),
    'pagination': () => import(/* webpackChunkName: 'ListForm' */ './Pagination.vue')
  },
  props: {
    visibleHeaderControls: { type: Boolean, default: true },
    config: { type: Object, required: true },
    formModel: { type: Function, required: true },
    // Prepare data
    prepareCreate: { type: Function, default: (data) => data },
    prepareSave: { type: Function, default: (data) => data },
    prepareGet: { type: Function, default: (data) => data },
    prepareClone: { type: Function, default: (data) => data },
    // Custom behaviour
    customDelete: { type: Function }
  },
  created () {
    this.$bus.$on(`Call-ListForm-Create-${this.config.id}`, this.create)
  },
  beforeDestroy () {
    this.$bus.$off(`Call-ListForm-Create-${this.config.id}`, this.create)
  },
  data () {
    return {
      model: null,
      dataOrigin: [],
      data: [],
      isLoading: false,
      requestGetDataDebounce: this.$_.debounce(this.requestGetData, 75),
      // Private
      userFilters: ''
    }
  },
  computed: {
    permissions () {
      return this.$store.getters.get('permissions')
    },
    isAllowToEdit () {
      return this.permission(this.permissions, this.model.permission.edit)
    },
    isAddNew () {
      return this.permission(this.permissions, this.model.header.controls.addNew.permission) &&
        this.model.header.controls.addNew.isVisible
    },
    isAutoSave () {
      return this.isAllowToEdit && this.model.autoSave.isAutoSave
    },
    isVisibleControls () {
      return this.model.controls.isVisible
    },
    permissionControls () {
      return this.model.controls.list.reduce((result, { type, permission }) => {
        result[type] = this.isAllowToEdit && this.permission(this.permissions, permission)
        return result
      }, {})
    },
    mapOrigin () {
      return this.dataOrigin.reduce((result, item) => {
        result[item[this.model.store.mainField]] = item
        return result
      }, {})
    },
    querySelect () {
      let selected = this.model.store.get.selected
      if (Array.isArray(selected)) {
        return selected.join(',')
      } else {
        return selected
      }
    }
  },
  watch: {
    config: {
      immediate: true,
      handler (value) {
        this.parseModel(value)
        if (this.model.isAutoLoad) {
          this.requestGetDataDebounce()
        }
      }
    },
    dataOrigin: {
      immediate: true,
      handler () {
        this.$emit('onChangeDataOrigin', this.dataOrigin)
      }
    }
  },
  methods: {
    // USER INTERFACE
    getData () {
      return [ ...this.data ]
    },
    /**
     * @desc Set filters to get data
     * @param filters
     */
    setFilters (filters = '') {
      this.userFilters = filters
    },
    /**
     * @desc Clear data of list form
     */
    clear () {
      this.dataOrigin = []
      this.data = []
    },
    /**
     * @desc Request load data
     */
    loadData (isAsync) {
      this.requestGetDataDebounce(!!isAsync)
    },
    refresh (isAsync) {
      this.requestGetDataDebounce(!!isAsync)
    },
    exportToExcel ({ columns = [], name = this.model.export.excel.name, isAll = false, WbName = this.model.id } = {}) {
      let { url, params } = this.createGetRequest()
      if (isAll) {
        params['take'] = undefined
        params['skip'] = undefined
      }
      return this._requestGetData({ url, params })
        .then((data) => {
          return this._exportExcelCreate({ name, data: data.value, WbName })
        })
    },
    _exportExcelCreate ({ name, data, WbName }) {
      let columns = this.model.export.excel.columns.map((item) => {
        let column = this.copyObjectProps(models.gerExportExcel(), item)
        column['title'] = this.checkEs6AndRun(column['title'])
        return column
      })
      name = this.checkEs6AndRun(name || this.model.id)
      return ExcelExporter.export(name, columns, data, WbName)
    },

    createGetRequest () {
      let url = `${this.model.store.get.url}`
      let { size, isVisible: isPagination, current } = this.model.footer.pagination
      const filters = []
      this.model.store.get.filters && filters.push(this.model.store.get.filters)
      this.userFilters && filters.push(this.userFilters)
      let orderBy = []
      this.model.store.get.orderBy.field && orderBy.push(this.model.store.get.orderBy.field)
      this.model.store.get.orderBy.order && orderBy.push(this.model.store.get.orderBy.order)

      let params = createParams({
        select: this.querySelect,
        filter: filters.join(' && '),
        expand: this.model.store.get.expand,
        skip: isPagination ? (current - 1) * size : undefined,
        take: isPagination ? size : undefined,
        orderBy: orderBy.join(' '),
        count: true
      })
      return {
        url,
        params
      }
    },
    setLoading (isLoading) {
      this.isLoading = isLoading
    },
    // END USER INTERFACE
    handleAutoSaveSuccess (_dataItem) {
      const itemID = _dataItem[this.model.store.mainField]
      this._requestGetItem({ itemID })
        .then((updatedItem) => {
          this.updateOrigin(updatedItem)
        })
    },
    updateOrigin (_dataItem) {
      this.dataOrigin = this.dataOrigin
        .map((originItem) => {
          if (originItem[this.model.store.mainField] === _dataItem[this.model.store.mainField]) {
            return this.copyObjectProps(originItem, _dataItem)
          } else {
            return originItem
          }
        })

      this.data = this.data.map((previousDataItem) => {
        if (previousDataItem[this.model.store.mainField] === _dataItem[this.model.store.mainField]) {
          return this.copyObjectProps(previousDataItem, _dataItem)
        } else {
          return previousDataItem
        }
      })
    },
    handleAutoSaveError () {
      this.dataOrigin = this.dataOrigin.map((dataOrigin) => ({ ...dataOrigin }))
      this.data = this.data.map((previousDataItem) => ({ ...previousDataItem }))
    },
    parseModel (config) {
      let model = this.$_.merge(models.getListForm(this), config)
      // Parse Header
      model.header.title.value = this.checkEs6AndRun(model.header.title.value)
      this.model = model
    },
    // Prepare Data
    parseData (data) {
      if (this.model.group.isAvailable) {
        data = this.groupData(data, this.model.group)
      }
      return data
    },
    groupData (data, config) {
      data.sort((a, b) => {
        return a[config.field] - b[config.field]
      })
      return data.map((dataItem, i) => {
        let nextItemField = data[i + 1] && data[i + 1][config.field]
        let previousField = data[i - 1] && data[i - 1][config.field]
        let id = dataItem[config.field]
        return {
          ...dataItem,
          _group: {
            id,
            isFirstOfGroup: (id !== previousField),
            isLastOfGroup: (id !== nextItemField)
          }
        }
      })
    },
    /**
     * @desc Prepare date to server
     * @param data - item of list form
     * @returns {Object} - prepared data
     */
    prepareDataToServer (data) {
      const model = this.formModel()
      return keepToModel(model, data)
    },
    // PAGINATION
    handlerPagination (data) {
      this.model.footer.pagination = {
        ...this.model.footer.pagination,
        ...data
      }
      this.requestGetDataDebounce()
    },
    // callback for update item
    handleUpdateDataItem (itemID, itemData) {
      let itemIndex = this.data
        .findIndex(item => item[this.model.store.mainField] === itemID)
      if (itemIndex !== -1) {
        let data = { ...this.data[itemIndex], ...itemData }
        this.$set(this.data, itemIndex, data)
        this.$emit('onUpdateDataItem', data)
      }
    },
    // COMMANDS
    handlerCommandControls (command, item) {
      switch (command) {
        case 'addNew':
          this.addNew()
          break
        case 'save':
          this.save(item)
          break
        case 'delete':
          this.delete(item)
          break
        case 'clone':
          this.clone(item)
          break
        default:
          this.$emit('command', command, item)
      }
    },
    addNew () {
      this.$router.replace({ query: Object.assign({}, this.$route.query, { [this.model.id]: 'new' }) })
      this.$bus.$emit('on-add-new-list-form', { list: this.model.id })
    },
    create (data, callback) {
      this.requestCreateData(data, callback)
    },
    clone (item) {
      this.requestCloneData(item)
    },
    save (item) {
      const validateCallback = (result) => {
        if (result instanceof Error) {
          console.error(result)
        } else {
          this.requestSaveData(item)
        }
      }
      this.$bus.$emit(`Call-ListForm-ValidateItem-${this.config.id}-${item[this.model.store.mainField]}`, validateCallback)
    },
    delete (item) {
      this.$refs['dialogDelete'].show(item)
    },
    _setDataSync (data, dataOrigin) {
      return Promise.resolve()
        .then(() => {
          this.dataOrigin = dataOrigin
          this.data = data
        })
    },
    handleDeleteConfirm (data, callback) {
      // TODO: Custom delete
      if (this.customDelete) {
        return this.customDelete(data, callback)
      }
      this.requestDeleteData(data, callback)
    },
    handleDeleteCancel (data, callback) {
    },
    // REQUESTS
    requestCloneData (data, callback = function () {}) {
      this.isLoading = true
      data = this.prepareClone(data)
      return this._requestCreateData(data)
        .then((response) => {
          callback(response)
          this.handlerClone(response.data)
          this.requestGetData()
        })
        .catch(error => {
          callback(error)
          this.requestErrorHandler(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    requestCreateData (data, callback = function () {}) {
      this.isLoading = true
      data = this.prepareCreate(data)
      return this._requestCreateData(data)
        .then((response) => {
          callback(response)
          this.handlerCreate(response.data)
          this.requestGetData()
        })
        .catch(error => {
          callback(error)
          this.requestErrorHandler(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    _requestCreateData (data) {
      let data_ = this.prepareDataToServer(data)
      data_ = JSON.parse(JSON.stringify(data_))
      delete data_[this.model.store.mainField]

      return api
        .post(this.checkJsTemplate(this.model.store.create.url, data_), data_)
    },
    requestSaveData (data) {
      this.isLoading = true
      data = this.prepareDataToServer(data)
      data = this.prepareSave(data)
      let { url, method } = this.model.store.patch
      url = `${this.checkEs6AndRun(url, data)}`
      delete data[this.model.store.mainField]
      return api(
        {
          url,
          method,
          data
        })
        .then(() => {
          this.handlerSave(data)
          return this.requestGetData()
        })
        .catch(error => {
          this.requestErrorHandler(error)
          this.isLoading = false
        })
    },
    requestDeleteData (item) {
      this.isLoading = true
      let { url } = this.model.store.delete
      url = `${this.checkEs6AndRun(url, item)}`
      return api
        .delete(url)
        .then((response) => {
          this.handlerDelete(response.data)
          this.requestGetData()
        })
        .catch(error => {
          this.requestErrorHandler(error)
        })
        .then(() => {
          this.isLoading = false
        })
    },
    requestGetData () {
      this.isLoading = true
      let { url, params } = this.createGetRequest()
      return this._requestGetData({ url, params })
        .then((data) => {
          let _data = this.prepareGet(data.value)
          let dataOrigin = Array.from(_data)
          let parsedData = this.parseData(_data)
          let waitRender = this._setDataSync(parsedData, dataOrigin)
          return waitRender.then(() => {
            this.handlerGet(_data)
            return data
          })
        })
        .then(({ count }) => {
          this.model.footer.pagination.total = count
        })
        .catch((error) => {
          this.requestErrorHandler(error)
        })
        .finally(() => {
          return this.$nextTick()
        })
        .then(() => {
          this.isLoading = false
        })
    },
    _requestGetData ({ url, params }) {
      params = JSON.parse(JSON.stringify(params))
      return api
        .get(url, { params })
        .then((response) => {
          if (!response.data.value) {
            return Promise.reject(new Error('No data value'))
          }
          return response.data
        })
    },
    async _requestGetItem ({ itemID }) {
      const { url, params } = this.createGetRequest()

      const params_ = createParams({
        select: params.select,
        filter: `${this.model.store.mainField} == ${itemID}`
      })

      return api
        .get(url, { params: params_ })
        .then((response) => {
          if (!response.data.value[0]) {
            return Promise.reject(new Error('No data value'))
          }
          return response.data.value[0]
        })
    },
    // Events
    /**
     * @param data - result of this.prepareGet()
     */
    handlerGet (data) {
      this.$emit('onGet', data)
    },
    handlerSave (data) {
      this.$emit('onSave', data)
    },
    handlerPatch (data) {
      this.$emit('onPatch', data)
    },
    handlerClone (data) {
      this.$emit('onClone', data)
    },
    handlerCreate (data) {
      this.$emit('onCreate', data)
    },
    handlerDelete (data) {
      this.$emit('onDelete', data)
    }
  }
}
</script>

<style lang="stylus">
  .list-form-wrap{
    height 100%
  }
  .list-form{
    height 100%
    display flex
    flex-direction column
  }
  .list-form__header{
    display flex
    justify-content space-between
    padding-bottom 2rem
    border-bottom 1px solid #eeeeef
  }
  .list-form__header-title{
    font-size 2.8rem
    color #65646c
    font-weight bold
    text-transform uppercase
  }
  .list-form__item{
    padding 2rem 0
    border-top 1px solid #eeeeef
    &.is-first-item{
      border-top none
    }
  }
</style>
